// Platzhalter für Auswertungen gesperrter Talente in Discover. Steht dort, wo
// sonst TalentMatchSection, TalentProfileSection oder TeamProfileSection
// stehen, damit für gesperrte Talente keine Ergebnis-Query und kein PDF läuft.
// Der Kopf mit Profilwahl und Schalter bleibt, so kommt man ohne Umweg zum
// Anforderungsprofil der Stelle.
//
// pending heißt, der Talentspeicher ist noch nicht gezählt. Dann stehen nur
// Skeletons da, ohne Hinweis auf eine Sperre, weil das Talent auch frei sein
// kann.
import { LockClosedIcon } from '@heroicons/react/16/solid'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import Skeleton from 'react-loading-skeleton'

import Button from '../../../components/tailwind/Button'
import { Heading } from '../../../components/ui/Heading'
import {
  LIMIT_DIALOG_ACTIONS,
  usePricingDialog
} from '../../pricing/pricing-dialog-context'
import { useDiscover } from '../hooks/useDiscover'
import { getProfileOptions } from '../utils'
import EnableFlexSwitch from './EnableFlexSwitch'
import ProfileDropdown from './ProfileDropdown'

export const LOCKED_TALENT_SOURCE = 'discover_selection'

export default function LockedTalentSection({
  className,
  pending = false,
  lockedCount = 1,
  variant = 'talent'
}) {
  const { t } = useTranslation()
  const {
    selectedResult,
    setSelectedResult,
    isFlexibleMatchingEnabled,
    setIsFlexibleMatchingEnabled
  } = useDiscover()
  const { openLimitDialog } = usePricingDialog()

  const profileOptions = useMemo(() => getProfileOptions(t), [t])
  const isTeam = variant === 'team'

  const onClickDetails = () =>
    openLimitDialog('talentStorage', {
      action: LIMIT_DIALOG_ACTIONS.INFO,
      source: LOCKED_TALENT_SOURCE
    })

  return (
    <div
      className={clsx('relative w-full rounded-lg bg-white py-6', className)}
      data-testid='discover-locked-talent'
      data-state={pending ? 'pending' : 'locked'}
    >
      <div className='border-b border-lightGrey px-6 pb-6'>
        <div className='flex flex-row items-start justify-between'>
          <div>
            <Heading level={6} className={'text-primaryBlue'}>
              {t('discover.talent_match_section.title')}
            </Heading>
            <ProfileDropdown
              className='mt-1'
              selectedProfile={selectedResult}
              profileOptions={profileOptions}
              onProfileChange={setSelectedResult}
              isLoading={pending}
            />
          </div>
          <EnableFlexSwitch
            enabled={isFlexibleMatchingEnabled}
            setEnabled={setIsFlexibleMatchingEnabled}
            isLoading={pending}
          />
        </div>
      </div>

      {pending ? (
        <div className='px-6 pt-8' aria-busy='true'>
          <span className='sr-only'>
            {t('pricing.talent_locked.pending_sr')}
          </span>
          <Skeleton count={4} />
        </div>
      ) : (
        <div className='px-6 pt-8'>
          <div className='mx-auto flex max-w-xl flex-col items-center rounded-lg p-8 text-center ring-1 ring-inset ring-gray-200'>
            <div className='flex size-12 items-center justify-center rounded-full bg-gray-100'>
              <LockClosedIcon
                aria-hidden='true'
                className='size-6 text-gray-500'
              />
            </div>
            <h3 className='mt-4 text-base font-semibold text-gray-900'>
              {isTeam
                ? t('pricing.limit.talent_storage.title_selection')
                : t('pricing.talent_locked.discover_title')}
            </h3>
            <p className='mt-2 text-sm text-gray-600'>
              {isTeam
                ? t('pricing.limit.talent_storage.text_locked', {
                    count: lockedCount
                  })
                : t('pricing.talent_locked.discover_text')}
            </p>
            <Button.SecondaryLG
              className='mt-6'
              text={t('pricing.banner.details_action')}
              onClick={onClickDetails}
            />
          </div>
        </div>
      )}
    </div>
  )
}

LockedTalentSection.propTypes = {
  className: PropTypes.string,
  pending: PropTypes.bool,
  lockedCount: PropTypes.number,
  variant: PropTypes.oneOf(['talent', 'team'])
}
